/**
 * Tiny, safe formula language for computed number fields, e.g. `{Damage} * {Attack speed}` or
 * `round({HP} / max({Cost}, 1), 2)`.
 *
 * - Stored form references fields by id (`{fld123}`) so renames never break formulas; the editor
 *   shows names (`{Damage}`). `toStoredExpression` / `toDisplayExpression` convert between the two.
 * - Grammar: numbers, `{field}`, `+ - * / % ^`, comparisons (`< <= > >= == !=` → 1/0),
 *   `&&`, `||`, `!`, parentheses and the functions in `FUNCTIONS`.
 * - Evaluation never throws: errors yield `{ ok: false }` and empty references yield `null`.
 */

type Token =
  | { t: 'num'; v: number }
  | { t: 'ref'; v: string }
  | { t: 'id'; v: string }
  | { t: 'op'; v: string }
  | { t: '('; v: '(' }
  | { t: ')'; v: ')' }
  | { t: ','; v: ',' };

export type FormulaNode =
  | { k: 'num'; v: number }
  | { k: 'ref'; id: string }
  | { k: 'unary'; op: '-' | '!' | '+'; a: FormulaNode }
  | { k: 'bin'; op: string; a: FormulaNode; b: FormulaNode }
  | { k: 'call'; fn: string; args: FormulaNode[] };

export class FormulaError extends Error {}

function tokenize(src: string): Token[] {
  const out: Token[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i]!;
    if (c === ' ' || c === '\t' || c === '\n') {
      i++;
      continue;
    }
    if (c === '{') {
      const end = src.indexOf('}', i + 1);
      if (end < 0) throw new FormulaError('Missing "}" after field reference');
      const name = src.slice(i + 1, end).trim();
      if (!name) throw new FormulaError('Empty field reference');
      out.push({ t: 'ref', v: name });
      i = end + 1;
      continue;
    }
    if (/[0-9.]/.test(c)) {
      const m = /^(\d+\.?\d*|\.\d+)(e[+-]?\d+)?/i.exec(src.slice(i));
      if (!m) throw new FormulaError(`Unexpected "${c}"`);
      out.push({ t: 'num', v: Number(m[0]) });
      i += m[0].length;
      continue;
    }
    if (/[a-zA-Z_]/.test(c)) {
      const m = /^[a-zA-Z_][a-zA-Z0-9_]*/.exec(src.slice(i))!;
      out.push({ t: 'id', v: m[0].toLowerCase() });
      i += m[0].length;
      continue;
    }
    const two = src.slice(i, i + 2);
    if (['<=', '>=', '==', '!=', '&&', '||'].includes(two)) {
      out.push({ t: 'op', v: two });
      i += 2;
      continue;
    }
    if ('+-*/%^<>!'.includes(c)) {
      out.push({ t: 'op', v: c });
      i++;
      continue;
    }
    if (c === '(' || c === ')' || c === ',') {
      out.push({ t: c, v: c } as Token);
      i++;
      continue;
    }
    throw new FormulaError(`Unexpected "${c}"`);
  }
  return out;
}

const BINARY_PRECEDENCE: Record<string, number> = {
  '||': 1,
  '&&': 2,
  '==': 3,
  '!=': 3,
  '<': 4,
  '<=': 4,
  '>': 4,
  '>=': 4,
  '+': 5,
  '-': 5,
  '*': 6,
  '/': 6,
  '%': 6,
  '^': 8,
};

export const FUNCTIONS: Record<string, { min: number; max: number; fn: (...a: number[]) => number; help: string }> = {
  min: { min: 1, max: 99, fn: (...a) => Math.min(...a), help: 'min(a, b, …) — smallest value' },
  max: { min: 1, max: 99, fn: (...a) => Math.max(...a), help: 'max(a, b, …) — largest value' },
  avg: { min: 1, max: 99, fn: (...a) => a.reduce((s, x) => s + x, 0) / a.length, help: 'avg(a, b, …) — average' },
  sum: { min: 1, max: 99, fn: (...a) => a.reduce((s, x) => s + x, 0), help: 'sum(a, b, …)' },
  round: {
    min: 1,
    max: 2,
    fn: (x, d = 0) => {
      const f = 10 ** Math.max(0, Math.min(10, Math.trunc(d)));
      return Math.round(x * f) / f;
    },
    help: 'round(x, decimals?)',
  },
  floor: { min: 1, max: 1, fn: Math.floor, help: 'floor(x)' },
  ceil: { min: 1, max: 1, fn: Math.ceil, help: 'ceil(x)' },
  abs: { min: 1, max: 1, fn: Math.abs, help: 'abs(x)' },
  sqrt: { min: 1, max: 1, fn: Math.sqrt, help: 'sqrt(x)' },
  pow: { min: 2, max: 2, fn: Math.pow, help: 'pow(x, y)' },
  log: { min: 1, max: 1, fn: Math.log10, help: 'log(x) — base 10' },
  ln: { min: 1, max: 1, fn: Math.log, help: 'ln(x)' },
  clamp: { min: 3, max: 3, fn: (x, lo, hi) => Math.min(Math.max(x, lo), hi), help: 'clamp(x, min, max)' },
  if: { min: 3, max: 3, fn: (c, a, b) => (c ? a : b), help: 'if(condition, then, else)' },
};

export function parseFormula(src: string): FormulaNode {
  const tokens = tokenize(src);
  let pos = 0;
  const peek = () => tokens[pos];
  const next = () => tokens[pos++];

  function primary(): FormulaNode {
    const tok = next();
    if (!tok) throw new FormulaError('Unexpected end of formula');
    switch (tok.t) {
      case 'num':
        return { k: 'num', v: tok.v };
      case 'ref':
        return { k: 'ref', id: tok.v };
      case '(': {
        const e = expr(0);
        if (next()?.t !== ')') throw new FormulaError('Missing ")"');
        return e;
      }
      case 'op':
        if (tok.v === '-' || tok.v === '+' || tok.v === '!') return { k: 'unary', op: tok.v, a: unaryOperand() };
        throw new FormulaError(`Unexpected "${tok.v}"`);
      case 'id': {
        if (tok.v === 'true') return { k: 'num', v: 1 };
        if (tok.v === 'false') return { k: 'num', v: 0 };
        if (tok.v === 'pi') return { k: 'num', v: Math.PI };
        const spec = FUNCTIONS[tok.v];
        if (!spec) throw new FormulaError(`Unknown function "${tok.v}"`);
        if (next()?.t !== '(') throw new FormulaError(`Expected "(" after ${tok.v}`);
        const args: FormulaNode[] = [];
        if (peek()?.t !== ')') {
          args.push(expr(0));
          while (peek()?.t === ',') {
            next();
            args.push(expr(0));
          }
        }
        if (next()?.t !== ')') throw new FormulaError(`Missing ")" after ${tok.v} arguments`);
        if (args.length < spec.min || args.length > spec.max) {
          throw new FormulaError(`${tok.v}() takes ${spec.min === spec.max ? spec.min : `${spec.min}–${spec.max}`} argument(s)`);
        }
        return { k: 'call', fn: tok.v, args };
      }
      default:
        throw new FormulaError(`Unexpected "${tok.v}"`);
    }
  }

  // Unary binds tighter than * but looser than ^ (so -2^2 = -4, like maths).
  function unaryOperand(): FormulaNode {
    return expr(7);
  }

  function expr(minPrec: number): FormulaNode {
    let left = primary();
    for (;;) {
      const tok = peek();
      if (!tok || tok.t !== 'op') break;
      const prec = BINARY_PRECEDENCE[tok.v];
      if (prec === undefined || prec < minPrec) break;
      next();
      // `^` is right-associative.
      const right = expr(tok.v === '^' ? prec : prec + 1);
      left = { k: 'bin', op: tok.v, a: left, b: right };
    }
    return left;
  }

  if (!tokens.length) throw new FormulaError('Formula is empty');
  const ast = expr(0);
  if (pos < tokens.length) throw new FormulaError(`Unexpected "${tokens[pos]!.v}"`);
  return ast;
}

export type FormulaResult = { ok: true; value: number | null } | { ok: false; error: string };

/**
 * Evaluate with `resolve(ref)` returning the numeric value of a referenced field (null = empty).
 * Any empty reference makes the result empty, mirroring spreadsheet behaviour for blank cells.
 */
export function evaluateFormula(ast: FormulaNode, resolve: (ref: string) => number | null | undefined): FormulaResult {
  let empty = false;
  const run = (n: FormulaNode): number => {
    switch (n.k) {
      case 'num':
        return n.v;
      case 'ref': {
        const v = resolve(n.id);
        if (v === null || v === undefined || Number.isNaN(v)) {
          empty = true;
          return 0;
        }
        return v;
      }
      case 'unary': {
        const a = run(n.a);
        return n.op === '-' ? -a : n.op === '!' ? (a ? 0 : 1) : a;
      }
      case 'bin': {
        const a = run(n.a);
        const b = run(n.b);
        switch (n.op) {
          case '+': return a + b;
          case '-': return a - b;
          case '*': return a * b;
          case '/': return a / b;
          case '%': return a % b;
          case '^': return a ** b;
          case '<': return a < b ? 1 : 0;
          case '<=': return a <= b ? 1 : 0;
          case '>': return a > b ? 1 : 0;
          case '>=': return a >= b ? 1 : 0;
          case '==': return a === b ? 1 : 0;
          case '!=': return a !== b ? 1 : 0;
          case '&&': return a && b ? 1 : 0;
          case '||': return a || b ? 1 : 0;
        }
        throw new FormulaError(`Unknown operator ${n.op}`);
      }
      case 'call':
        return FUNCTIONS[n.fn]!.fn(...n.args.map(run));
    }
  };
  try {
    const value = run(ast);
    if (empty) return { ok: true, value: null };
    if (!Number.isFinite(value)) return { ok: true, value: null };
    return { ok: true, value };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

/** Collect referenced ids/names (as written in the formula). */
export function formulaRefs(ast: FormulaNode, out = new Set<string>()): Set<string> {
  if (ast.k === 'ref') out.add(ast.id);
  else if (ast.k === 'unary') formulaRefs(ast.a, out);
  else if (ast.k === 'bin') {
    formulaRefs(ast.a, out);
    formulaRefs(ast.b, out);
  } else if (ast.k === 'call') ast.args.forEach((a) => formulaRefs(a, out));
  return out;
}

interface NamedField {
  id: string;
  name: string;
}

/** `{Damage} * 2` → `{fld_x} * 2`. Returns the unresolved names so the editor can flag them. */
export function toStoredExpression(display: string, fields: readonly NamedField[]): { expression: string; unknown: string[] } {
  const unknown: string[] = [];
  const byName = new Map(fields.map((f) => [f.name.trim().toLowerCase(), f.id]));
  const ids = new Set(fields.map((f) => f.id));
  const expression = display.replace(/\{([^}]*)\}/g, (whole, raw: string) => {
    const name = raw.trim();
    const id = byName.get(name.toLowerCase()) ?? (ids.has(name) ? name : undefined);
    if (!id) {
      unknown.push(name);
      return whole;
    }
    return `{${id}}`;
  });
  return { expression, unknown };
}

/** `{fld_x} * 2` → `{Damage} * 2`. Unknown ids render as `{?}`. */
export function toDisplayExpression(stored: string, fields: readonly NamedField[]): string {
  const byId = new Map(fields.map((f) => [f.id, f.name]));
  return stored.replace(/\{([^}]*)\}/g, (_whole, id: string) => `{${byId.get(id.trim()) ?? '?'}}`);
}

const parseCache = new Map<string, FormulaNode | FormulaError>();

/** Parse with a small memo (formulas are re-evaluated for every row on every render). */
export function parseFormulaCached(expression: string): FormulaNode | FormulaError {
  let hit = parseCache.get(expression);
  if (!hit) {
    try {
      hit = parseFormula(expression);
    } catch (e) {
      hit = e instanceof FormulaError ? e : new FormulaError(String(e));
    }
    if (parseCache.size > 500) parseCache.clear();
    parseCache.set(expression, hit);
  }
  return hit;
}
